'use client'

import { useRef, useEffect, useState } from 'react'
import { SolutionBySlugDTO } from '@/lib/models/model-types/solution/solution.types'
import Image from 'next/image'
import { Markdown } from '@/lib/ui-kit/components/Markdown.component'
import { motion, useTransform, useScroll } from 'framer-motion'

export const Title = ({ children }: { children?: React.ReactNode }) => {
  return <h2 className="heading-xl pb-6 xl:pb-10">{children}</h2>
}

export const Text = ({ children }: { children?: React.ReactNode }) => {
  return <p className="paragraph-l pb-16">{children}</p>
}

const Arrow = ({
  color1,
  color2,
  id,
  progress,
  range,
}: {
  color1: string
  color2: string
  id: string
  progress: any
  range: [number, number]
}) => {
  const strokeDasharray = useTransform(progress, range, ['0, 200', '200, 200'])
  const strokeDashoffset = useTransform(progress, range, [-200, 0])

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 200 24"
      strokeWidth="1.5"
      className="w-48 xl:w-56"
    >
      <defs>
        <linearGradient id={id}>
          <stop offset="0%" stopColor={color1} />
          <stop offset="100%" stopColor={color2} />
        </linearGradient>
      </defs>
      <motion.path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M182.25 8.25 186 12m0 0-3.75 3.75M186 12H0"
        stroke={`url(#${id})`}
        strokeDasharray={strokeDasharray}
        strokeDashoffset={strokeDashoffset}
      />
    </svg>
  )
}

export const SectionWalkthrough = ({
  data,
  apiAssetsUrl,
}: {
  data: SolutionBySlugDTO
  apiAssetsUrl: string | undefined
}) => {
  const { walkthroughHeading, walkthroughSubheading, walkthroughCard } = data

  const targetRef = useRef<HTMLDivElement | null>(null)
  const [endValue, setEndValue] = useState('-75%')
  const [colors, setColors] = useState<string[][]>([])

  const { scrollYProgress } = useScroll({
    // @ts-ignore
    target: targetRef,
  })

  const x = useTransform(scrollYProgress, [0, 1], ['0', endValue])

  const calculateEndValue = () => {
    if (walkthroughCard && walkthroughCard.length > 0 && targetRef.current) {
      const cardWidth = 300
      const cardPadding = 64
      const totalCardsWidth =
        walkthroughCard.length * (cardWidth + cardPadding) - cardPadding
      const containerWidth = targetRef.current.clientWidth

      const requiredScroll = totalCardsWidth - containerWidth

      // bit of buffer since the computed value is not always exact
      const adjustedScroll = requiredScroll + cardWidth

      const requiredScrollPercentage = (adjustedScroll / containerWidth) * 100

      setEndValue(`-${requiredScrollPercentage}%`)
    }
  }

  const generateColors = (
    startColor: string,
    endColor: string,
    walkthrough: number,
  ) => {
    const interpolate = (color1: string, color2: string, factor: number) => {
      const result = color1
        .slice(1)
        .match(/.{2}/g)!
        .map((hex, i) => {
          const color1Int = parseInt(hex, 16)
          const color2Int = parseInt(color2.slice(1).match(/.{2}/g)![i], 16)
          const colorResult = Math.round(
            color1Int + factor * (color2Int - color1Int),
          )
            .toString(16)
            .padStart(2, '0')
          return colorResult
        })
        .join('')
      return `#${result}`
    }

    const colorArray = []
    for (let i = 0; i < walkthrough; i++) {
      const factor = i / (walkthrough - 1)
      colorArray.push(interpolate(startColor, endColor, factor))
    }
    return colorArray
  }

  useEffect(() => {
    calculateEndValue()
    window.addEventListener('resize', calculateEndValue)
    return () => {
      window.removeEventListener('resize', calculateEndValue)
    }
  }, [])

  useEffect(() => {
    if (walkthroughCard && walkthroughCard.length > 0) {
      const generatedColors = generateColors(
        '#37B7FF',
        '#E9009A',
        walkthroughCard.length + 1,
      )
      const colorPairs = []
      for (let i = 0; i < generatedColors.length - 1; i++) {
        colorPairs.push([generatedColors[i], generatedColors[i + 1]])
      }
      setColors(colorPairs)
    }
  }, [walkthroughCard])

  return (
    <section
      ref={targetRef}
      className="relative m-auto h-[300vh] max-w-[1440px] xl:h-[300vh]"
    >
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden px-6 xl:px-10">
        {walkthroughHeading && (
          <Markdown components={{ p: Title }}>{walkthroughHeading}</Markdown>
        )}
        <div className="grid grid-cols-1 xl:grid-cols-6">
          <div className="col-span-1 xl:col-span-2">
            {walkthroughSubheading && (
              <Markdown components={{ p: Text }}>
                {walkthroughSubheading}
              </Markdown>
            )}
          </div>
        </div>
        {walkthroughCard && walkthroughCard.length > 0 && (
          // @ts-ignore
          <motion.div style={{ x }} className="relative flex gap-10 xl:gap-16">
            {walkthroughCard.map(({ title, description, icon }, index) => {
              const cardOpacity = useTransform(
                scrollYProgress,
                [
                  index / walkthroughCard.length - 0.2,
                  (index + 1) / walkthroughCard.length - 0.2,
                ],
                [0, 1],
              )
              const cardScale = useTransform(
                scrollYProgress,
                [
                  index / walkthroughCard.length - 0.2,
                  (index + 1) / walkthroughCard.length - 0.2,
                ],
                [0.9, 1],
              )

              const arrowRange: [number, number] = [
                (index - 1) / walkthroughCard.length,
                index / walkthroughCard.length,
              ]

              return (
                <div key={index} className="relative flex items-start">
                  {index > 0 && colors[index - 1] && (
                    <motion.div
                      style={{ opacity: cardOpacity, scale: cardScale }}
                      transition={{
                        type: 'spring',
                        stiffness: 100,
                        damping: 20,
                        duration: 0.6,
                        delay: index * 0.1,
                      }}
                      // @ts-ignore
                      className="absolute left-[-12.5rem] top-[9.25rem] xl:left-[-14.5rem] xl:top-[9.75rem]"
                    >
                      <Arrow
                        color1={colors[index - 1][0]}
                        color2={colors[index - 1][1]}
                        id={`grad${index}`}
                        progress={scrollYProgress}
                        range={arrowRange}
                      />
                    </motion.div>
                  )}
                  <motion.div
                    style={{ opacity: cardOpacity, scale: cardScale }}
                    transition={{
                      type: 'spring',
                      stiffness: 100,
                      damping: 20,
                      duration: 0.6,
                      delay: index * 0.1,
                    }}
                    // @ts-ignore
                    className="flex w-[18.75rem] flex-col items-start"
                  >
                    <Image
                      src={`${apiAssetsUrl || ''}${icon.url}`}
                      width={32}
                      height={32}
                      alt={icon.alternativeText ?? `Step ${index + 1} icon`}
                      className="mb-4 object-cover"
                    />
                    <h3 className="paragraph-xxl mb-10">{title}</h3>
                    <div className="text-69xl leading-none">
                      {index + 1 < 10 ? `0${index + 1}.` : `${index + 1}.`}
                    </div>
                    <p className="paragraph-s mt-10">{description}</p>
                  </motion.div>
                </div>
              )
            })}
          </motion.div>
        )}
      </div>
    </section>
  )
}
